import { isValidPhoneNumber } from "libphonenumber-js";
import { useEffect } from "react";
import { Controller, useForm } from "react-hook-form";
import { Button, DropdownSelect, ErrorMessage, Modal, PhoneNumberInput, TextInput, Typography } from "@/components/atoms";
import { getErrorMessages } from "@/helpers/reduxHelpers";
import {
  useAddFavouritePassengerMutation,
  useUpdateFavouritePassengerMutation,
} from "@/redux/apis/favouritePassenger/favouritePassengerApi";
import { FavouritePassenger } from "@/redux/apis/favouritePassenger/types";
import { addToast, showSnackbar } from "@/utils";
import { EMAIL_PATTERN, TITLE_OPTIONS } from "../guestForm";

interface GuestFormData {
  title: string;
  firstName: string;
  lastName: string;
  contactNumber: string;
  email: string;
}

interface AddEditGuestModalProps {
  open: boolean;
  mode: "add" | "edit";
  guest?: FavouritePassenger;
  accountId?: string;
  onClose: () => void;
}

export const AddEditGuestModal = ({ open, mode, guest, accountId, onClose }: AddEditGuestModalProps) => {
  const {
    control,
    register,
    handleSubmit,
    reset,
    formState: { errors },
  } = useForm<GuestFormData>({
    defaultValues: { title: "", firstName: "", lastName: "", contactNumber: "", email: "" },
  });

  const [addPassenger, { isLoading: isAdding }] = useAddFavouritePassengerMutation();
  const [updatePassenger, { isLoading: isUpdating }] = useUpdateFavouritePassengerMutation();
  const isLoading = isAdding || isUpdating;

  useEffect(() => {
    if (!open) return;
    reset({
      title: guest?.title ?? "",
      firstName: guest?.first_name ?? "",
      lastName: guest?.last_name ?? "",
      contactNumber: guest?.phone ?? "",
      email: guest?.email ?? "",
    });
  }, [open, guest, reset]);

  const onSubmit = (form: GuestFormData) => {
    const body = {
      title: form.title,
      first_name: form.firstName.trim(),
      last_name: form.lastName.trim(),
      phone: form.contactNumber,
      email: form.email.trim(),
    };
    const action =
      mode === "edit" && guest ? updatePassenger({ ...body, uuid: guest.uuid, accountId }) : addPassenger({ ...body, accountId });
    action
      .unwrap()
      .then(() => {
        showSnackbar({
          size: "small",
          duration: 5000,
          message: `Successfully ${mode === "edit" ? "updated" : "added"} guest ${body.first_name} ${body.last_name}`,
        });
        onClose();
      })
      .catch((error) => addToast("danger", getErrorMessages(error)[0]));
  };

  return (
    <Modal
      open={open}
      onClose={onClose}
      variant="web-medium"
      title={mode === "edit" ? "Edit Guest Passenger" : "Add Guest Passenger"}
      description={mode === "add" ? "Add a guest passenger to quickly include them in your trip bookings." : undefined}
      renderButtons={() => (
        <>
          <Button variant="tertiary" size="md" onClick={onClose} disabled={isLoading}>
            Cancel
          </Button>
          <Button variant="primary" size="md" onClick={handleSubmit(onSubmit)} disabled={isLoading}>
            {mode === "edit" ? "Save" : "Add Guest"}
          </Button>
        </>
      )}
    >
      <form className="grid grid-cols-1 gap-x-5 gap-y-6 sm:grid-cols-2" onSubmit={handleSubmit(onSubmit)}>
        <div className="flex flex-col gap-1">
          <Typography variant="action" className="text-neutral-dark-gray">
            Title
          </Typography>
          <Controller
            name="title"
            control={control}
            rules={{ required: "Title not selected" }}
            render={({ field }) => (
              <DropdownSelect
                className="w-full"
                hasError={!!errors.title}
                placeholder="Select Title"
                options={TITLE_OPTIONS}
                value={field.value}
                onChange={(value) => field.onChange(value)}
              />
            )}
          />
          <ErrorMessage errors={errors} name="title" />
        </div>
        {/* Row 1's right cell is intentionally empty (matches the Figma layout). */}
        <div className="hidden sm:block" />

        <div className="flex flex-col gap-1">
          <label htmlFor="guest-first-name">
            <Typography variant="action" className="text-neutral-dark-gray">
              First Name
            </Typography>
          </label>
          <TextInput
            id="guest-first-name"
            placeholder="Enter First Name"
            hasError={!!errors.firstName}
            {...register("firstName", { required: "First Name is required" })}
          />
          <ErrorMessage errors={errors} name="firstName" />
        </div>

        <div className="flex flex-col gap-1">
          <label htmlFor="guest-last-name">
            <Typography variant="action" className="text-neutral-dark-gray">
              Last Name
            </Typography>
          </label>
          <TextInput
            id="guest-last-name"
            placeholder="Enter Last Name"
            hasError={!!errors.lastName}
            {...register("lastName", { required: "Last Name is required" })}
          />
          <ErrorMessage errors={errors} name="lastName" />
        </div>

        <div className="flex flex-col gap-1">
          <Typography variant="action" className="text-neutral-dark-gray">
            Contact Number
          </Typography>
          <PhoneNumberInput
            control={control}
            name="contactNumber"
            hasError={!!errors.contactNumber}
            rules={{
              required: "Please fill out a phone number",
              validate: {
                checkNumber: (value: string) => isValidPhoneNumber(value ?? "") || "Invalid phone number",
              },
            }}
          />
          <ErrorMessage errors={errors} name="contactNumber" />
        </div>

        <div className="flex flex-col gap-1">
          <label htmlFor="guest-email">
            <Typography variant="action" className="text-neutral-dark-gray">
              Email Address
            </Typography>
          </label>
          <TextInput
            id="guest-email"
            type="email"
            placeholder="Enter Email Address"
            hasError={!!errors.email}
            {...register("email", {
              required: "Email is required",
              pattern: { value: EMAIL_PATTERN, message: "Invalid email address" },
            })}
          />
          <ErrorMessage errors={errors} name="email" />
        </div>
      </form>
    </Modal>
  );
};
